import React from 'react';
import { Link } from 'react-router-dom';
import {
  ClockIcon,
  BookOpenIcon,
  UsersIcon,
  StarIcon,
} from '../../../components/common/IconsComponent';

const courses = [
  {
    id: 1,
    category: 'Programming',
    title: 'Python Programming Fundamentals',
    teacher: 'Dr. Ahmed Hassan',
    description: 'Master Python from scratch. Learn variables, data types, functions, OOP, and build real-world projects.',
    duration: '60 min',
    sessions: 12,
    students: 47,
    price: 35,
    rating: 4.8,
    image: 'https://images.unsplash.com/photo-1526379095098-d400fd0bf935?auto=format&fit=crop&w=1200&q=80',
    mode: null,
  },
  {
    id: 2,
    category: 'Mathematics',
    title: 'Advanced Mathematics - Calculus',
    teacher: 'Prof. Sarah Cohen',
    description: 'Deep dive into calculus concepts including limits, derivatives, integrals, and their applications.',
    duration: '90 min',
    sessions: 16,
    students: 32,
    price: 40,
    rating: 4.9,
    image: 'https://images.unsplash.com/photo-1509228468518-180dd4864904?auto=format&fit=crop&w=1200&q=80',
    mode: null,
  },
  {
    id: 3,
    category: 'Languages',
    title: 'Arabic Language for Beginners',
    teacher: 'Fatima Al-Rashid',
    description: 'Learn Modern Standard Arabic from native speakers. Focus on reading, writing, speaking, and listening.',
    duration: '45 min',
    sessions: 20,
    students: 28,
    price: 30,
    rating: 4.7,
    image: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?auto=format&fit=crop&w=1200&q=80',
    mode: 'Private',
  },
];

const CourseCard = ({ id, category, title, teacher, description, duration, sessions, students, price, rating, image, mode }) => (
  <article className="flex flex-col overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-100 transition duration-300 hover:-translate-y-1 hover:shadow-lg">

    {/* Image */}
    <div className="relative">
      <img
        src={image}
        alt={title}
        className="h-48 w-full object-cover sm:h-56"
        loading="lazy"
      />

      {/* Badges */}
      <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
        <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
          {category}
        </span>
        {mode && (
          <span className="rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-semibold text-orange-700">
            {mode}
          </span>
        )}
      </div>

      {/* Rating */}
      <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-sm">
        <StarIcon className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" aria-hidden="true" />
        {rating}
      </div>

      {/* Title overlay */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-4">
        <h3 className="text-base font-bold leading-snug text-white sm:text-lg">
          {title}
        </h3>
      </div>
    </div>

    {/* Body */}
    <div className="flex flex-1 flex-col p-4 sm:p-5">
      <p className="text-sm font-medium text-slate-700">{teacher}</p>

      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-slate-500">
        {description}
      </p>

      {/* Meta */}
      <div className="mt-4 flex flex-wrap gap-3 text-xs text-slate-400 sm:text-sm">
        <div className="flex items-center gap-1">
          <ClockIcon className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{duration}</span>
        </div>
        <div className="flex items-center gap-1">
          <BookOpenIcon className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{sessions} sessions</span>
        </div>
        <div className="flex items-center gap-1">
          <UsersIcon className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{students} students</span>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
        <div className="flex items-baseline gap-1">
          <span className="text-2xl font-bold text-emerald-600 sm:text-3xl">${price}</span>
          <span className="text-xs text-slate-400">/session</span>
        </div>
        <Link
          to={`/courses/${id}`}
          className="rounded-xl bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700 sm:px-5 sm:py-2.5 sm:text-sm"
        >
          View Details
        </Link>
      </div>
    </div>

  </article>
);

const FeaturedCourses = () => (
  <section className="bg-white py-12 lg:py-20">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

      {/* Header */}
      <div className="mb-10 text-center sm:mb-12">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
          Featured Courses
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-base text-slate-500 sm:mt-4 sm:text-lg">
          Explore our most popular courses taught by industry experts and experienced educators
        </p>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {courses.map((course) => (
          <CourseCard key={course.id} {...course} />
        ))}
      </div>

    </div>
  </section>
);

export default FeaturedCourses;